// components/BuySellPopup.js
import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio, FormControl, Select, MenuItem
} from '@mui/material';
import './css/BuySellPopup.css';
import BidOfferTable from '@marketwatch/gridcomponents/guiinstrumentrow/MoreDetails/comp/BidOfferTable';
import {
    setSubscriptionToDetails,
    setSubscriptionToQuote,
    findInstrumentTokenForExchange
} from '../services/apiService';
import { createPortal } from 'react-dom';
import { DataContext } from '@dashboard/contexts/DataContext';
import AdvancedOrderPopup from './advancedOrderPopup/AdvancedOrderPopup';
import { apiFetch as fetch } from "apiClient";

// ✅ NEW: use a dedicated TickProvider for the depth token
import { TickProvider, useTickContext } from '@dashboard/contexts/TickContext';
import {LoginContext} from "../../../LoginContext";
import { useStackedPopup } from "@dashboard/contexts/PopupStackContext";
import {submitAdvancedOrder} from "@dashboard/buysellpopup/advancedOrderPopup/submitAdvancedOrder"; //

async function fetchQuote(token) {
    try {
        const res = await fetch(`/api/kite-quotes/live/${token}`);
        if (!res.ok) return null;
        return await res.json();
    } catch (e) {
        console.error("Quote fetch error", e);
        return null;
    }
}

function getPreviousWorkingDay(date) {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    let day = d.getDay();
    if (day === 0) d.setDate(d.getDate() - 2); // sunday → friday
    if (day === 6) d.setDate(d.getDate() - 1); // saturday → friday
    return d;
}

// Small helper that reads tick from the *inner* TickProvider and
// converts it to bids/offers for BidOfferTable
const DepthPanel = () => {
    const { tick } = useTickContext();

    const defaultLevels = useMemo(
        () => Array(5).fill({ price: 0, orders: 0, quantity: 0 }),
        []
    );

    const { bids, offers } = useMemo(() => {
        if (!tick) {
            return { bids: defaultLevels, offers: defaultLevels };
        }

        const buyLevels =
            tick.depth?.buy?.map(b => ({
                price: b.price,
                orders: b.orders,
                quantity: b.quantity
            })) || defaultLevels;

        const sellLevels =
            tick.depth?.sell?.map(o => ({
                price: o.price,
                orders: o.orders,
                quantity: o.quantity
            })) || defaultLevels;

        return { bids: buyLevels, offers: sellLevels };
    }, [tick, defaultLevels]);

    return <BidOfferTable bids={bids} offers={offers} />;
};

const BuySellPopup = ({
                          open,
                          side,             // 'BUY' | 'SELL'
                          instrument,       // GUIInstrument from context
                          tick,             // tick from TickContext (for the row)
                          onClose,
                          onSubmit,         // async function(orderPayload) => { success, message }
                          defaultZerodhaUserId
                      }) => {
    const [segment, setSegment] = useState('NSE');  // NSE / BSE toggle for order + depth
    const [qty, setQty] = useState('1');
    const [orderType, setOrderType] = useState('LIMIT'); // LIMIT / MARKET
    const [price, setPrice] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [depthPosition, setDepthPosition] = useState(null);
    const [depthOpen, setDepthOpen] = useState(false);
    const [depthInstrumentToken, setDepthInstrumentToken] = useState(null);

    const [advancedOpen, setAdvancedOpen] = useState(false); // advanced popup
    const [advancedBackendError, setAdvancedBackendError] = useState(''); // backend error shown inside advanced
    const [advancedSubmitting, setAdvancedSubmitting] = useState(false);   // disable Done while validating
    const { zerodhaConnectionStatusDTO} = useContext(LoginContext);
    const [activeZerodhaUsers, setActiveZerodhaUsers] = useState(null);
    const { allInstrumentsList } = useContext(DataContext);
    const [fetchedQuote, setFetchedQuote] = useState(null);
    const [selectedZerodhaUserId, setSelectedZerodhaUserId] = useState('');
    const [successFlashOpen, setSuccessFlashOpen] = useState(false);
    const [successFlashMsg, setSuccessFlashMsg] = useState('');

    useStackedPopup(true, () => {
        // Only need to close the menu; no external onClose prop required
        onClose();
    });

    const getEffectiveInstrumentToken = () =>
        findInstrumentTokenForExchange(
            instrument.tradingsymbol,
            segment,
            allInstrumentsList
        ) || depthInstrumentToken || instrument.instrumentToken;


    const availableExchanges = useMemo(() => {
        if (!instrument || !instrument.tradingsymbol || !Array.isArray(allInstrumentsList)) {
            // fallback – behave like old behaviour
            return ['NSE', 'BSE'];
        }

        const tsLower = instrument.tradingsymbol.toLowerCase();

        const matches = allInstrumentsList.filter(inst =>
            inst &&
            inst.tradingsymbol &&
            inst.exchange &&
            inst.tradingsymbol.toLowerCase() === tsLower &&
            (inst.exchange.toUpperCase() === 'NSE' || inst.exchange.toUpperCase() === 'BSE')
        );

        const set = new Set(matches.map(m => m.exchange.toUpperCase()));
        if (set.size === 0) {
            // if nothing found, keep old behaviour
            return ['NSE', 'BSE'];
        }

        // Return in a stable order (NSE first, then BSE) but only if present
        const result = [];
        if (set.has('NSE')) result.push('NSE');
        if (set.has('BSE')) result.push('BSE');
        return result;
    }, [instrument, allInstrumentsList]);


    // Initialisation when popup opens
    useEffect(() => {
        if (!open) return;

        // Default segment: if instrument.exchange is valid & available, use it,
        // otherwise fall back to the first available exchange.
        const instrumentEx = instrument?.exchange?.toUpperCase();
        let initialSegment;
        if (instrumentEx && availableExchanges.includes(instrumentEx)) {
            initialSegment = instrumentEx;
        } else if (availableExchanges.length > 0) {
            initialSegment = availableExchanges[0];
        } else {
            initialSegment = 'NSE'; // last-resort fallback
        }

        setSuccessFlashOpen(false);
        setSuccessFlashMsg('');

        setSegment(initialSegment);
        setQty('1');
        setOrderType('LIMIT');

        if (tick && typeof tick.lastTradedPrice !== 'undefined') {
            setPrice(String(tick.lastTradedPrice));
        } else {
            setPrice('');
        }

        setError('');
        setSuccess('');
        setDepthOpen(false);

        setAdvancedOpen(false);
        setAdvancedBackendError('');
        setAdvancedSubmitting(false);

        const token =
            findInstrumentTokenForExchange(
                instrument.tradingsymbol,
                initialSegment,
                allInstrumentsList
            ) || instrument.instrumentToken;

        setDepthInstrumentToken(token);
        setActiveZerodhaUsers(zerodhaConnectionStatusDTO.activeZerodhaUserIds);
        setSelectedZerodhaUserId(defaultZerodhaUserId || '');
    }, [open, instrument, allInstrumentsList, availableExchanges, zerodhaConnectionStatusDTO, tick]);

    useEffect(() => {
        if (!open) return;
        if (!availableExchanges.includes(segment) && availableExchanges.length > 0) {
            setSegment(availableExchanges[0]);
        }
    }, [availableExchanges, segment, open]);

    // When the user switches BSE/NSE, pick the corresponding instrumentToken
    // AND, if depth is already open, move the depth subscription to the new token.
    useEffect(() => {
        if (!open) return;

        const tokenForSegment =
            findInstrumentTokenForExchange(
                instrument.tradingsymbol,
                segment,
                allInstrumentsList
            ) || instrument.instrumentToken;

        setDepthInstrumentToken(prevToken => {
            const prev = prevToken;

            // If depth popup is currently open and token actually changes,
            // move the “details” subscription from old token to new token.
            if (depthOpen && prev && prev !== tokenForSegment) {
                setSubscriptionToQuote(prev, "BUYSELLPOPUP");
                if (tokenForSegment) {
                    setSubscriptionToDetails(tokenForSegment, "BUYSELLPOPUP");
                }
            }

            return tokenForSegment;
        });
    }, [segment, open, instrument, allInstrumentsList, depthOpen]);

    // When orderType changes, handle price enabling/disabling
    useEffect(() => {
        if (orderType === 'MARKET') {
            setPrice('0');
        } else if (tick && typeof tick.lastTradedPrice !== 'undefined') {
            setPrice(String(tick.lastTradedPrice));
        }
    }, [orderType, tick]);

    const closeDepth = () => {
        if (depthOpen && depthInstrumentToken) {
            setSubscriptionToQuote(depthInstrumentToken, "BUYSELLPOPUP");
        }
        setDepthOpen(false);
    };


    const handleDepthButtonClick = (event) => {
        if (depthOpen) {
            // toggle OFF
            closeDepth();
        } else {
            // toggle ON – position based on button
            if (event?.currentTarget) {
                const rect = event.currentTarget.getBoundingClientRect();
                setDepthPosition({
                    top: rect.top,        // align vertically with the Depth chip
                    left: rect.right + 12 // a small gap to the right
                });
            } else {
                setDepthPosition(null);
            }

            setDepthOpen(true);
            if (depthInstrumentToken) {
                setSubscriptionToDetails(depthInstrumentToken,"BUYSELLPOPUP");
            }
        }
    };

    const handleDialogClose = () => {
        closeDepth(); // also turns off details subscription if needed
        onClose();
    };

    const handleDialogMuiClose = (event, reason) => {
        // Ignore all backdrop clicks so depth panel stays open
        if (reason === 'backdropClick') {
            return;
        }
        handleDialogClose();
    };

    const handleOrderTypeChange = (event) => {
        setOrderType(event.target.value);
    };

    const handleSubmit = async () => {
        setError('');
        setSuccess('');

        const quantityNum = Number(qty);
        const priceNum = Number(price);

        if (!quantityNum || quantityNum <= 0) {
            setError('Quantity must be > 0');
            return;
        }
        if (orderType === 'LIMIT' && (!price || isNaN(priceNum) || priceNum <= 0)) {
            setError('Price must be > 0 for LIMIT orders');
            return;
        }
        if (!selectedZerodhaUserId) {
            setError('Please select a Zerodha User.');
            return;
        }

        const payload = {
            exchange: segment,                         // NSE / BSE
            tradingsymbol: instrument.tradingsymbol,
            instrumentToken: getEffectiveInstrumentToken(),
            transactionType: side,                    // BUY / SELL
            quantity: quantityNum,
            price: orderType === 'MARKET' ? 0 : priceNum,
            orderType,                                // LIMIT / MARKET
            product: 'CNC',                           // Longterm only
            validity: 'DAY',                          // only Day
            variety: 'REGULAR',
            zerodhaUserId: selectedZerodhaUserId,  // ✅ NEW
            tag: "Regular"
        };

        try {
            setLoading(true);
            const result = await onSubmit(payload);   // expect { success, message, orderId? }
            if (result?.success) {
                const msg = result.message || 'Order placed successfully.';
                setSuccess(msg);

                // NEW: show flash overlay, hide form
                setSuccessFlashMsg(msg);
                setSuccessFlashOpen(true);

                // Auto close after 2 seconds
                window.setTimeout(() => {
                    handleDialogClose();
                }, 1200);
            } else {
                setError(result?.message || 'Order failed.');
            }

        } catch (e) {
            console.error(e);
            setError('Unexpected error while placing order.');
        } finally {
            setLoading(false);
        }
    };

    const isMarket = orderType === 'MARKET';
    const headerClass =
        side === 'BUY' ? 'bsp-header bsp-header-buy' : 'bsp-header bsp-header-sell';
    const primaryButtonLabel = side === 'BUY' ? 'Buy' : 'Sell';

    // ----- Drag handling (header symbol area is the handle) -----
    const handleHeaderMouseDown = (e) => {
        // Start dragging only from the drag zone
        e.preventDefault();
        e.stopPropagation();

        // Find the MUI Paper DOM element that wraps this header
        const paper = e.currentTarget.closest('.MuiPaper-root');
        if (!paper) return;

        const rect = paper.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        // If depth is open, remember its offset from the dialog at drag start
        let depthOffsetX = null;
        let depthOffsetY = null;
        if (depthOpen && depthPosition) {
            depthOffsetX = depthPosition.left - rect.left;
            depthOffsetY = depthPosition.top - rect.top;
        }

        // Ensure the paper is position: fixed so we can move it freely
        paper.style.position = 'fixed';
        paper.style.margin = '0';
        paper.style.transform = 'none';

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            moveEvent.stopPropagation();

            const newLeft = moveEvent.clientX - offsetX;
            const newTop = moveEvent.clientY - offsetY;

            paper.style.left = `${newLeft}px`;
            paper.style.top = `${newTop}px`;

            // Move depth panel along with the dialog, keeping same relative gap
            if (depthOpen && depthOffsetX !== null && depthOffsetY !== null) {
                setDepthPosition({
                    top: newTop + depthOffsetY,
                    left: newLeft + depthOffsetX
                });
            }
        };

        const handleMouseUp = (upEvent) => {
            upEvent.preventDefault();
            upEvent.stopPropagation();

            window.removeEventListener('mousemove', handleMouseMove, true);
            window.removeEventListener('mouseup', handleMouseUp, true);
        };

        window.addEventListener('mousemove', handleMouseMove, true);
        window.addEventListener('mouseup', handleMouseUp, true);
    };

    const handleAdvancedConfirm = async (config) => {
        const result = await submitAdvancedOrder(config, {
            instrument,
            segment,
            depthInstrumentToken: getEffectiveInstrumentToken(),
            existingOrder: null   // always create mode here
        });

        if (!result.success) {
            setAdvancedBackendError(result.message);
            return;
        }

        // Close advanced overlay
        setAdvancedOpen(false);
        setAdvancedBackendError("");
        setAdvancedSubmitting(false);

        // OPTIONAL: show flash message (nice UX) then close BuySell
        setSuccessFlashMsg(result.message || "Advanced order created.");
        setSuccessFlashOpen(true);
        setError("");

        // Close BuySell (and depth subscription) shortly after showing success
        window.setTimeout(() => {
            handleDialogClose(); // <-- closes BuySellPopup too
        }, 800);
    };


    const lastClosePrice =
        fetchedQuote?.ohlc?.close ?? fetchedQuote?.lastPrice ?? null;

    const lastClosePriceDateObj = fetchedQuote?.timestamp
        ? getPreviousWorkingDay(new Date(fetchedQuote.timestamp))
        : null;

    const lastClosePriceDate = lastClosePriceDateObj
        ? lastClosePriceDateObj.toISOString().slice(0, 10)
        : null;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleDialogMuiClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    className: `bsp-dialog ${side === 'BUY' ? 'bsp-buy' : 'bsp-sell'}`
                }}
                disableEscapeKeyDown
                disableRestoreFocus
                disableEnforceFocus
                // prevent clicks / drags inside the dialog from reaching ReactGrid
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
            >
                <DialogTitle
                    className={headerClass}
                    onMouseDown={handleHeaderMouseDown}
                >
                    <div className="bsp-header-left">
                        <div className="bsp-header-dragzone"></div>

                        <div className="bsp-header-symbol">
                            <span className="bsp-symbol">{instrument.tradingsymbol}</span>
                        </div>
                    </div>

                    <div className="bsp-header-right">
                        <div className="bsp-header-segment-radio">
                            <RadioGroup
                                row
                                value={segment}
                                onChange={(e) => setSegment(e.target.value)}
                            >
                                {availableExchanges.map(ex => (
                                    <FormControlLabel
                                        key={ex}
                                        value={ex}
                                        control={<Radio size="small" />}
                                        label={ex}
                                    />
                                ))}
                            </RadioGroup>

                        </div>

                        <div className="bsp-header-tools">
                            <button
                                type="button"
                                className="bsp-header-chip bsp-header-chip-advanced"
                                onClick={async () => {
                                    setAdvancedBackendError('');
                                    setAdvancedSubmitting(false);

                                    // resolve token safely
                                    let token =
                                        findInstrumentTokenForExchange(
                                            instrument.tradingsymbol,
                                            segment,
                                            allInstrumentsList
                                        ) ||
                                        depthInstrumentToken ||
                                        instrument.instrumentToken;

                                    console.log("FETCHING QUOTE FOR TOKEN:", token);

                                    const quote = await fetchQuote(token);

                                    if (quote) {
                                        setFetchedQuote(quote);
                                    } else {
                                        console.warn("QUOTE NULL for token:", token);
                                        setFetchedQuote(null);
                                    }

                                    setAdvancedOpen(true);
                                }}

                            >
                                Advanced
                            </button>
                            <button
                                type="button"
                                className={`bsp-header-chip bsp-header-chip-depth ${depthOpen ? 'active' : ''}`}
                                onClick={handleDepthButtonClick}
                            >
                                Depth
                            </button>
                        </div>
                    </div>
                </DialogTitle>

                <DialogContent className="bsp-content">
                    {successFlashOpen ? (
                        <div className={`bsp-success-flash ${side === 'BUY' ? 'buy' : 'sell'}`}>
                            <div className="bsp-success-flash-title">Order Placed</div>
                            <div className="bsp-success-flash-sub">{successFlashMsg}</div>
                            <div className="bsp-success-flash-hint">Closing…</div>
                        </div>
                    ) : (
                        <>
                    {/* Regular / Longterm strip */}
                    <div className="bsp-header-flex">
                        <div className="bsp-strip-row">
                            <div className="bsp-strip bsp-strip-regular active">Regular</div>
                        </div>

                        <div className="bsp-row bsp-row-radio">
                            <div className="bsp-radio-group">
                                <span className="bsp-longterm-label active">Longterm (CNC)</span>
                            </div>
                        </div>

                        {/* NEW: Zerodha user dropdown (required) */}
                        <div className="bsp-zerodha-user-box">
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedZerodhaUserId}
                                    onChange={(e) => setSelectedZerodhaUserId(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        height: 32,
                                        '& .MuiSelect-select': {
                                            paddingTop: '6px',
                                            paddingBottom: '6px',
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Zerodha User</em>
                                    </MenuItem>

                                    {(activeZerodhaUsers || []).map((uid) => (
                                        <MenuItem key={uid} value={uid}>{uid}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>


                    {/* Qty & Price row */}
                    <div className="bsp-row">
                        <div className="bsp-field">
                            <label>Qty.</label>
                            <TextField
                                size="small"
                                type="number"
                                value={qty}
                                onChange={e => setQty(e.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </div>

                        <div className="bsp-field">
                            <label>Price</label>
                            <TextField
                                size="small"
                                type="number"
                                value={price}
                                disabled={isMarket}
                                onChange={e => setPrice(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Market / Limit */}
                    <div className="bsp-row bsp-row-radio">
                        <RadioGroup
                            row
                            value={orderType}
                            onChange={handleOrderTypeChange}
                            className="bsp-radio-group"
                        >
                            <FormControlLabel value="MARKET" control={<Radio />} label="Market" />
                            <FormControlLabel value="LIMIT" control={<Radio />} label="Limit" />
                        </RadioGroup>
                    </div>

                    {/* Validity + fake disclosed qty */}
                    <div className="bsp-row">
                        <div className="bsp-field">
                            <label>Validity</label>
                            <div className="bsp-validity-pill">Day</div>
                        </div>
                        <div className="bsp-field">
                            <label>Disclosed qty.</label>
                            <TextField
                                size="small"
                                type="number"
                                value="0"
                                disabled
                            />
                        </div>
                    </div>

                    {/* Error / Success */}
                    {error && <div className="bsp-message bsp-error">{error}</div>}
                    {success && <div className="bsp-message bsp-success">{success}</div>}
                        </>
                    )}
                </DialogContent>

                <DialogActions className="bsp-actions">
                    <Button onClick={handleDialogClose} disabled={loading || successFlashOpen}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading || successFlashOpen}
                        className={`bsp-primary-btn ${side === 'BUY' ? 'bsp-primary-buy' : 'bsp-primary-sell'}`}
                    >
                        {primaryButtonLabel}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Depth side popup – rendered via portal, positioned next to Depth chip */}
            {depthOpen && depthInstrumentToken &&
                createPortal(
                    <TickProvider
                        instrumentToken={depthInstrumentToken}
                        key={depthInstrumentToken}
                    >
                        <div
                            className="bsp-depth-panel collapsed"
                            style={depthPosition ? { top: depthPosition.top, left: depthPosition.left } : undefined}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                        >
                            <DepthPanel />
                        </div>
                    </TickProvider>,
                    document.body
                )}

            {/* Advanced overlay – keeps main popup + depth visible underneath */}
            <AdvancedOrderPopup
                open={advancedOpen}
                side={side}
                tradingsymbol={instrument?.tradingsymbol}
                basePrice={price ? Number(price) : (tick?.lastTradedPrice ?? null)}
                onClose={() => {
                    setAdvancedOpen(false);
                    setAdvancedBackendError('');
                    setAdvancedSubmitting(false);
                }}
                onConfirm={handleAdvancedConfirm}
                backendError={advancedBackendError}
                submitting={advancedSubmitting}
                activeZerodhaUsers={activeZerodhaUsers} // NEW: pass users list down
                lastClosePrice={lastClosePrice}
                lastClosePriceDate={lastClosePriceDate}
            />
        </>
    );
};

export default BuySellPopup;
